import { generateErrorId, extractOriginalMessage, ensureRetry } from './errorHandlerBase';
import store from '../index';
import { addCentralizedError, showErrorModal, DLOError, DLO_API_NAMES } from '../slices/dloSlice';

/**
 * Utility function to handle DLO API errors in a centralized way
 * This function creates a DLOError object and adds it to the centralized error state
 * It also optionally shows the error modal if displayModal is true
 * 
 * @param apiName - The name of the API that failed (use DLO_API_NAMES constants)
 * @param error - The error object or string received from the API
 * @param displayModal - Whether to show the error modal immediately (default: true)
 * @param context - Additional context information for debugging
 * @returns The created DLOError object with a unique ID
 */
export const handleDLOError = (
  apiName: string,
  error: any,
  displayModal: boolean = true,
  context?: any
): DLOError => {
  // Extract a normalized error message
  const errorMessage = extractOriginalMessage(error) || `An error occurred in ${apiName}`;

  // Create the DLOError object
  const dloError: DLOError = {
    id: generateErrorId(),
    apiName,
    message: errorMessage,
    timestamp: Date.now(),
    context: context || {
      originalError: error,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown'
    }
  };

  // Add to centralized error state
  store.dispatch(addCentralizedError(dloError));

  // Optionally show the error modal
  if (displayModal) {
    store.dispatch(showErrorModal(dloError));
  }

  // Log to console for development
  console.error(`[DLO Error] ${apiName}:`, {
    message: errorMessage,
    errorId: dloError.id,
    originalError: error,
    context: dloError.context
  });

  return dloError;
};

/**
 * Wrapper function for async thunks that handles errors gracefully
 * This prevents promise rejections that would bubble up to ErrorBoundary
 * 
 * @param apiCall - The API function to call
 * @param apiName - The name of the API (use DLO_API_NAMES constants)
 * @param displayModal - Whether to show error modal on failure
 * @param context - Additional context for error tracking
 * @returns Promise that always resolves, either with data or null
 */
export const safeApiCall = async <T>(
  apiCall: () => Promise<T>,
  apiName: string,
  displayModal: boolean = true,
  context?: any
): Promise<T | null> => {
  try {
    const result = await apiCall();
    return result;
  } catch (error) {
    handleDLOError(apiName, error, displayModal, context);
    // Return null instead of throwing to prevent ErrorBoundary trigger
    return null;
  }
};

/**
 * Helper function to create user-friendly error messages based on API name
 * This provides consistent messaging across the application
 * 
 * @param apiName - The name of the API that failed
 * @param originalMessage - The original error message from the API
 * @returns A user-friendly error message
 */
export const getUserFriendlyErrorMessage = (apiName: string, originalMessage: string): string => {
  const friendlyMessages: Record<string, string> = {
    [DLO_API_NAMES.FETCH_DASHBOARD_SCHEDULE]: 'Unable to load schedule data. Please try again.',
    [DLO_API_NAMES.FETCH_KCPH_DATA]: 'Unable to load KCPH data. Please try again.',
    [DLO_API_NAMES.FETCH_DASHBOARD_HOURS]: 'Unable to load hours data. Please try again.',
    [DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_DAILY]: 'Unable to load forecast daily data. Please try again.',
    [DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_ACTUAL_DAILY]: 'Unable to load actual daily data. Please try again.',
    [DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_FORECAST_DAILY]: 'Unable to save forecast changes. Please try again.',
    [DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_ACTUAL_DAILY]: 'Unable to save actual data changes. Please try again.',
    [DLO_API_NAMES.SAVE_SCHEDULE_DATA]: 'Unable to save schedule. Please try again.',
    [DLO_API_NAMES.FETCH_EMPLOYEES]: 'Unable to load employee data. Please try again.',
    [DLO_API_NAMES.CLEAN_SCHEDULE_SETTINGS]: 'Unable to clean schedule settings. Please try again.',
  };

  return  `Unable to fetch the data. ${originalMessage || 'Please try again.'}` || friendlyMessages[apiName];
};

/**
 * Enhanced error handler that creates user-friendly messages
 * and handles both display and logging
 * 
 * @param apiName - The name of the API that failed
 * @param error - The error object or string
 * @param displayModal - Whether to show error modal
 * @param context - Additional context
 * @returns The created DLOError object
 */
export const handleDLOErrorWithFriendlyMessage = (
  apiName: string,
  error: any,
  displayModal: boolean = true,
  context?: any
): DLOError => {
  // Get the original error message
  const originalMessage = extractOriginalMessage(error);

  // Get user-friendly message
  const friendlyMessage = getUserFriendlyErrorMessage(apiName, originalMessage);

  // Create enhanced error object
  const dloError: DLOError = {
    id: generateErrorId(),
    apiName,
    message: friendlyMessage,
    timestamp: Date.now(),
    context: {
      originalMessage,
      ...context,
      originalError: error,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown'
    }
  };

  // Add to centralized error state
  store.dispatch(addCentralizedError(dloError));

  // Optionally show the error modal
  if (displayModal) {
    store.dispatch(showErrorModal(dloError));
  }

  // Log to console for development
  console.error(`[DLO Error] ${apiName}:`, {
    userFriendlyMessage: friendlyMessage,
    originalMessage: originalMessage,
    errorId: dloError.id,
    originalError: error,
    context: dloError.context
  });

  return dloError;
};

/**
 * Type guard to check if a thunk action was fulfilled
 * This helps with type safety when checking thunk results
 */
export const isThunkFulfilled = (action: any): boolean => {
  return action.type && action.type.endsWith('/fulfilled');
};

/**
 * Type guard to check if a thunk action was rejected
 * This helps with type safety when checking thunk results
 */
export const isThunkRejected = (action: any): boolean => {
  return action.type && action.type.endsWith('/rejected');
};